import { z } from 'zod';
import { FX_CURRENCY_PAIRS, type FxCurrencyPair } from '@metycle/shared';
import type { EditHedgeRequestPayload } from '../api/edit-request';
import type { RaiseHedgeRequestPayload } from '../api/raise';
import { decimalString, emptyToUndefined, optionalDecimal, optionalText } from './form-helpers';

const optionalQpMonth = z.preprocess(
  emptyToUndefined,
  z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Use YYYY-MM')
    .optional(),
);

// The precise LME prompt date the request targets. A native date input yields
// YYYY-MM-DD; it maps to the request's target_prompt_date (a full DATE), so
// unlike the coarse QP month it carries the exact day.
const optionalPromptDate = z.preprocess(
  emptyToUndefined,
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .optional(),
);

export const RAISE_METAL_OPTIONS = ['CU', 'AL', 'ZN', 'NI', 'PB', 'SN'] as const;
// The full launchpad reason set (minus the legacy "Unpricing" alias). Price Fix /
// Fix Reversal / Roll can be raised standalone here; the dedicated /roll and
// /price-fix endpoints still handle the source-linked follow-on flows.
//
// Values are the Prisma `hedge_request_reason` enum KEYS: the backend Zod
// validates these, and the hedge-book API serialises reason as the key. The form
// holds the key directly and submits it as-is — no label↔key translation. The
// human-readable labels live with the dropdown options in the field layer.
export const RAISE_REASON_VALUES = [
  'Physical_Sale_Pricing',
  'Physical_Purchase_Pricing',
  'Price_Fix',
  'Fix_Reversal',
  'Roll',
  'Pre_lending',
  'Pre_borrowing',
] as const;
export const RAISE_DIRECTIONS = ['Buy', 'Sell'] as const;
export const RAISE_INSTRUMENTS = ['Future', 'Option', 'FX'] as const;
export const RAISE_SCOPES = ['ORDER', 'BL'] as const;
// Same pricing references and target-price currencies as the launchpad drawer.
export const RAISE_REFERENCES = ['LME_CASH', 'LME_3M', 'COMEX', 'SHFE'] as const;
export const RAISE_CURRENCIES = ['USD', 'EUR', 'GBP'] as const;

interface RefineInput {
  scopeLevel: 'ORDER' | 'BL';
  orderId?: string | undefined;
  blOrderId?: string | undefined;
  instrumentType: 'Future' | 'Option' | 'FX';
  hedgeMetal?: 'CU' | 'AL' | 'ZN' | 'NI' | 'PB' | 'SN' | undefined;
  currencyPair?: FxCurrencyPair | undefined;
}

function refineScope(value: RefineInput, ctx: z.RefinementCtx): void {
  if (value.scopeLevel === 'ORDER' && (!value.orderId || value.orderId.length === 0)) {
    ctx.addIssue({
      code: 'custom',
      path: ['orderId'],
      message: 'Order ID is required for ORDER scope',
    });
  }
  if (value.scopeLevel === 'BL' && (!value.blOrderId || value.blOrderId.length === 0)) {
    ctx.addIssue({
      code: 'custom',
      path: ['blOrderId'],
      message: 'BL ID is required for BL scope',
    });
  }
}

function refineInstrument(value: RefineInput, ctx: z.RefinementCtx): void {
  if (value.instrumentType === 'FX') {
    if (!value.currencyPair || value.currencyPair.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['currencyPair'],
        message: 'Currency pair is required for FX',
      });
    }
    if (value.hedgeMetal) {
      ctx.addIssue({
        code: 'custom',
        path: ['hedgeMetal'],
        message: 'Metal must not be set for FX',
      });
    }
  } else if (!value.hedgeMetal) {
    ctx.addIssue({ code: 'custom', path: ['hedgeMetal'], message: 'Metal is required' });
  }
}

export const raiseFormSchema = z
  .object({
    scopeLevel: z.enum(RAISE_SCOPES),
    orderId: optionalText(100),
    blOrderId: optionalText(50),
    direction: z.enum(RAISE_DIRECTIONS),
    quantityMt: decimalString,
    reason: z.enum(RAISE_REASON_VALUES),
    instrumentType: z.enum(RAISE_INSTRUMENTS),
    hedgeMetal: z.preprocess(emptyToUndefined, z.enum(RAISE_METAL_OPTIONS).optional()),
    // Closed list: a free-typed pair could detach the hedge from its deal (#949).
    currencyPair: z.preprocess(emptyToUndefined, z.enum(FX_CURRENCY_PAIRS).optional()),
    reference: z.enum(RAISE_REFERENCES),
    targetPrice: optionalDecimal,
    targetPriceCurrency: z.enum(RAISE_CURRENCIES),
    estimatedQpMonth: optionalQpMonth,
    targetPromptDate: optionalPromptDate,
    brokerPreference: optionalText(80),
    notes: optionalText(2000),
  })
  .superRefine((value, ctx) => {
    refineScope(value, ctx);
    refineInstrument(value, ctx);
  });

export type RaiseFormValues = z.infer<typeof raiseFormSchema>;

export function defaultRaiseValues(): RaiseFormValues {
  return {
    scopeLevel: 'ORDER',
    orderId: '',
    blOrderId: '',
    direction: 'Sell',
    quantityMt: '',
    reason: 'Physical_Sale_Pricing',
    instrumentType: 'Future',
    hedgeMetal: 'CU',
    currencyPair: undefined,
    reference: 'LME_CASH',
    targetPrice: '',
    targetPriceCurrency: 'USD',
    estimatedQpMonth: '',
    targetPromptDate: '',
    brokerPreference: '',
    notes: '',
  };
}

function trimmedOrUndefined(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

export function buildRaisePayload(values: RaiseFormValues): RaiseHedgeRequestPayload {
  const payload: RaiseHedgeRequestPayload = {
    scopeLevel: values.scopeLevel,
    direction: values.direction,
    quantityMt: values.quantityMt,
    reason: values.reason,
    instrumentType: values.instrumentType,
    // No LME reference on FX raises; the backend default applies.
    ...(values.instrumentType === 'FX' ? {} : { reference: values.reference }),
    ...buildScopePayloadFields(values),
    ...buildInstrumentPayloadFields(values),
  };
  assignOptionalFields(payload, values);
  return payload;
}

// Identity (scope, links) and the creation snapshot are immutable on edit, so the
// edit payload is the raise payload minus the scope/order/BL fields.
export function buildEditPayload(values: RaiseFormValues): EditHedgeRequestPayload {
  const payload: EditHedgeRequestPayload = {
    direction: values.direction,
    quantityMt: values.quantityMt,
    reason: values.reason,
    instrumentType: values.instrumentType,
    ...(values.instrumentType === 'FX' ? {} : { reference: values.reference }),
    ...buildInstrumentPayloadFields(values),
  };
  assignOptionalFields(payload, values);
  return payload;
}

function buildScopePayloadFields(values: RaiseFormValues): Partial<RaiseHedgeRequestPayload> {
  if (values.scopeLevel === 'ORDER') {
    return values.orderId ? { orderId: values.orderId } : {};
  }
  const parsed = Number.parseInt(values.blOrderId ?? '', 10);
  return Number.isFinite(parsed) ? { blOrderId: parsed } : {};
}

function buildInstrumentPayloadFields(values: RaiseFormValues): Partial<RaiseHedgeRequestPayload> {
  if (values.instrumentType === 'FX' && values.currencyPair) {
    return { currencyPair: values.currencyPair };
  }
  if (values.hedgeMetal) return { hedgeMetal: values.hedgeMetal };
  return {};
}

interface OptionalPayloadFields {
  targetPrice?: string;
  targetPriceCurrency?: string;
  estimatedQpMonth?: string;
  targetPromptDate?: string;
  brokerPreference?: string;
  notes?: string;
}

function assignOptionalFields(payload: OptionalPayloadFields, values: RaiseFormValues): void {
  const target = trimmedOrUndefined(values.targetPrice);
  if (target) {
    payload.targetPrice = target;
    // An FX target is a rate denominated by the pair itself, so it has no
    // separate currency — don't persist the hidden USD default as a phantom unit.
    if (values.instrumentType !== 'FX') {
      payload.targetPriceCurrency = values.targetPriceCurrency;
    }
  }
  const qp = trimmedOrUndefined(values.estimatedQpMonth);
  if (qp) payload.estimatedQpMonth = qp;
  const promptDate = trimmedOrUndefined(values.targetPromptDate);
  if (promptDate) payload.targetPromptDate = promptDate;
  const broker = trimmedOrUndefined(values.brokerPreference);
  if (broker) payload.brokerPreference = broker;
  const notes = trimmedOrUndefined(values.notes);
  if (notes) payload.notes = notes;
}
